import {AppLanguage, useDisplayMobile} from '@/logged/common/composables';
import {useNavigationTabs} from '@/logged/features/aside-menu/composables';
import {useHeaderNavigationTabs} from '@/logged/features/header-menu/composables';
import {useUser} from '@/logged/features/user';
import {defineStore} from 'pinia';
import {computed, ref, watch} from 'vue';
import {useFreelancerTourDefinition, useTourDefinition} from '.';
import type {TourDefinition} from './onboarding-existing-user.types';

export const useOnboardingExistingUser = defineStore('onboarding-existing-user-store', () => {
    const userStore = useUser();
    const {filteredNavigationRoutes} = useNavigationTabs();
    const headerNavigationTabs = useHeaderNavigationTabs();
    const {isDisplayMobile} = useDisplayMobile();

    const allTourDefinition = ref<TourDefinition[]>([]);

    const isFreelanceIdentity = computed(() => userStore.hasSelectedFreelanceIdentity);

    function onHandleTours() {
        if (isFreelanceIdentity.value) {
            allTourDefinition.value = useFreelancerTourDefinition().value;
        } else {
            allTourDefinition.value = useTourDefinition().value;
        }
    }

    watch(isFreelanceIdentity, onHandleTours);
    /* We need it to run it once (onHandleTours) and it works because there isn't any reactive values inside this composables */
    onHandleTours();

    const selectedOnboardingTourStepIndex = ref(0);
    const selectedOnboardingTourStep = computed(() => activeTours.value[selectedOnboardingTourStepIndex.value]);
    const showExistingUserOnboardingTour = computed(() => showClientOnBoardingTour.value || showFreelancerOnBoardingTour.value);

    const showClientOnBoardingTour = computed(
        () => userStore.hasSelectedClientIdentity && !userStore.user?.options.newUser && !userStore.user?.options.onboarded && !isDisplayMobile.value,
    );

    const showFreelancerOnBoardingTour = computed(
        () =>
            window.location.pathname === '/dashboard/freelancer' &&
            userStore.hasSelectedFreelanceIdentity &&
            !userStore.user?.options.onboarded &&
            !isDisplayMobile.value,
    );

    const selectedIllustration = computed<string | undefined>(() => {
        const selectedLanguage = userStore.user?.currentLanguage as AppLanguage;
        const illustrations = selectedOnboardingTourStep.value.illustrations;
        return illustrations[selectedLanguage] ?? illustrations[AppLanguage.FR];
    });

    /** Used for prefetching */
    const filteredIllustrationsByLanguage = computed<string[]>(() => {
        const selectedLanguage = userStore.user?.currentLanguage as AppLanguage;
        return allTourDefinition.value.map((m) => m.illustrations[selectedLanguage]).filter((f): f is string => !!f);
    });

    const allFreelancerPresentationGifs: Partial<Record<AppLanguage, string>> = {
        [AppLanguage.FR]: 'https://dam.malt.com/navbar/onboarding/freelancer-gifs/sidebar_FR.gif',
        [AppLanguage.EN]: 'https://dam.malt.com/navbar/onboarding/freelancer-gifs/sidebar_EN.gif',
        [AppLanguage.ES]: 'https://dam.malt.com/navbar/onboarding/freelancer-gifs/sidebar_ES.gif',
        [AppLanguage.DE]: 'https://dam.malt.com/navbar/onboarding/freelancer-gifs/sidebar_DE.gif',
        [AppLanguage.NL]: 'https://dam.malt.com/navbar/onboarding/freelancer-gifs/sidebar_NL.gif',
    };

    const allPresentationGifs: Partial<Record<AppLanguage, string>> = {
        [AppLanguage.FR]: 'https://dam.malt.com/navbar/onboarding/gifs/UI_Malt_16x9_FR.gif',
        [AppLanguage.EN]: 'https://dam.malt.com/navbar/onboarding/gifs/UI_Malt_16x9_EN.gif',
        [AppLanguage.ES]: 'https://dam.malt.com/navbar/onboarding/gifs/UI_Malt_16x9_ES.gif',
        [AppLanguage.DE]: 'https://dam.malt.com/navbar/onboarding/gifs/UI_Malt_16x9_DE.gif',
        [AppLanguage.NL]: 'https://dam.malt.com/navbar/onboarding/gifs/UI_Malt_16x9_NL.gif',
    };

    const displayedPresentationGif = computed(() => {
        const selectedLanguage = userStore.user?.currentLanguage as AppLanguage;
        if (isFreelanceIdentity.value) {
            return allFreelancerPresentationGifs[selectedLanguage];
        }
        return allPresentationGifs[selectedLanguage];
    });

    const activeTours = computed(() => {
        const activeNavbarIds = [...filteredNavigationRoutes.value, ...headerNavigationTabs.value].map((nav) => nav.id);
        return allTourDefinition.value.filter((tour) => activeNavbarIds.includes(tour.navTabId));
    });

    return {
        selectedOnboardingTourStep,
        selectedOnboardingTourStepIndex,
        selectedIllustration,
        showExistingUserOnboardingTour,
        displayedPresentationGif,
        activeTours,
        filteredIllustrationsByLanguage,
    };
});
